/**
 * Match a lookup against the fields of a record, not just its name.
 *
 * The agent sends the searchable part of a resource's field catalogue with
 * every lookup (`params.search_fields`), so which fields a record can be
 * named by - a MAC address, a serial number, a location, a security type -
 * is decided by the same catalogue the forms are built from. Nothing here
 * knows what a Site, an SSID or an Access Point is; adding a resource is a
 * catalogue edit, not a change to this file.
 *
 * A lookup that arrives without a catalogue behaves exactly as it did
 * before, so an older agent keeps working.
 */

const FIELD_ID_PATTERN = /^[a-z][a-z0-9_]{0,63}$/;
const ATTRIBUTE_PATTERN = /^[A-Za-z_][A-Za-z0-9_]{0,63}(\.[A-Za-z_][A-Za-z0-9_]{0,63})?$/;

// The reply is validated against a strict schema, so what travels back is
// bounded here rather than discovered to be too large on arrival.
const MAX_FIELDS = 24;
const MAX_VALUE_LENGTH = 256;

/**
 * Read one declared attribute off a Canvas row.
 * @param {object} row Canvas row
 * @param {string} path attribute name, optionally one level deep
 * @return {*} attribute value
 */
function readAttribute(row, path) {
	if (!row || typeof row !== 'object') return undefined;
	const [head, tail] = path.split('.');
	const value = row[head];
	if (tail === undefined) return value;
	return value && typeof value === 'object' ? value[tail] : undefined;
}

/**
 * Convert a stored value to the text a request would name it by.
 * @param {*} value stored value
 * @return {string|null} comparable text
 */
function storedText(value) {
	if (value == null || typeof value === 'object') return null;
	if (typeof value === 'boolean') return value ? 'true' : 'false';
	const text = String(value).trim();
	if (!text) return null;
	return text.length > MAX_VALUE_LENGTH ? text.slice(0, MAX_VALUE_LENGTH) : text;
}

/**
 * Keep only well-formed catalogue entries.
 * @param {*} searchFields field catalogue sent with the lookup
 * @return {Array<object>} usable field descriptors
 */
export function normalizeSearchFields(searchFields) {
	if (!Array.isArray(searchFields)) return [];
	return searchFields
		.filter((field) => (
			field &&
			typeof field === 'object' &&
			FIELD_ID_PATTERN.test(field.id || '') &&
			Array.isArray(field.attributes)
		))
		.map((field) => ({
			id: field.id,
			label: typeof field.label === 'string' && field.label
				? field.label
				: field.id,
			type: typeof field.type === 'string' ? field.type : 'string',
			frontendName: typeof field.frontend_name === 'string'
				? field.frontend_name
				: null,
			attributes: field.attributes.filter(
				(name) => typeof name === 'string' && ATTRIBUTE_PATTERN.test(name),
			),
			choices: Array.isArray(field.choices)
				? field.choices
					.filter((choice) => choice && typeof choice === 'object')
					.map((choice) => ({
						id: String(choice.id || ''),
						label: String(choice.label || ''),
						aliases: Array.isArray(choice.aliases)
							? choice.aliases.map(String)
							: [],
						values: Array.isArray(choice.values)
							? choice.values.map(String)
							: [],
					}))
				: [],
			valueLabels: field.value_labels && typeof field.value_labels === 'object'
				? field.value_labels
				: {},
		}))
		.filter((field) => field.attributes.length)
		.slice(0, MAX_FIELDS);
}

/**
 * Read the stored value of every catalogued field on one Canvas row.
 * @param {object} row Canvas row
 * @param {Array<object>} fields normalized field descriptors
 * @return {object} stored values keyed by field id
 */
export function readFieldValues(row, fields) {
	const values = {};
	fields.forEach((field) => {
		for (const attribute of field.attributes) {
			const text = storedText(readAttribute(row, attribute));
			if (text !== null) {
				values[field.id] = text;
				return;
			}
		}
	});
	return values;
}

/**
 * Attach catalogued stored values to a normalized candidate.
 * @param {object|null} candidate normalized candidate
 * @param {object} row Canvas row it came from
 * @param {Array<object>} fields normalized field descriptors
 * @return {object|null} candidate carrying its field values
 */
export function withFieldValues(candidate, row, fields) {
	if (!candidate || !fields.length) return candidate;
	const values = readFieldValues(row, fields);
	return Object.keys(values).length
		? {...candidate, field_values: values}
		: candidate;
}

/**
 * Compare a stored value against the way a request would say it.
 *
 * A record stores what the form submitted - `5` for WPA2 Enterprise, `1`
 * for a checked box. A request says "Enterprise" or "enabled". Both sides
 * are in the catalogue, so the words are read from there rather than
 * written here.
 *
 * @param {object} field normalized field descriptor
 * @param {string} stored stored value
 * @return {Array<string>} every way this value can be named
 */
function namesForValue(field, stored) {
	const names = [stored];
	const folded = stored.toLocaleLowerCase();
	field.choices.forEach((choice) => {
		const matches = choice.values.some((value) => value === stored) ||
			choice.id.toLocaleLowerCase() === folded ||
			choice.label.toLocaleLowerCase() === folded;
		if (matches) names.push(choice.label, choice.id, ...choice.aliases);
	});
	const labels = field.valueLabels[stored];
	if (Array.isArray(labels)) names.push(...labels.map(String));
	return names.filter(Boolean);
}

/**
 * Every value a candidate can be found by, in catalogue order.
 * @param {object} candidate normalized candidate
 * @param {Array<object>} fields normalized field descriptors
 * @return {Array<string>} comparable values
 */
export function candidateSearchValues(candidate, fields) {
	const values = candidate?.field_values || {};
	const named = fields
		.map((field) => {
			const stored = values[field.id];
			return typeof stored === 'string' && stored
				? namesForValue(field, stored)
				: [];
		})
		.flat();
	// The name still leads: a candidate whose name matches exactly must
	// outrank one that matches on some other field, and rank order follows
	// the order values are supplied in.
	return [candidate?.name, ...named].filter(Boolean);
}

/**
 * Build the value reader a ranked lookup uses.
 *
 * Falls back to the resource's own reader when no catalogue arrived, so a
 * lookup issued by an older agent ranks exactly as it always has.
 *
 * @param {Array<object>} fields normalized field descriptors
 * @param {Function|undefined} fallback resource value reader
 * @return {Function|undefined} value reader for rankLookupCandidates
 */
export function searchValueReader(fields, fallback) {
	if (!fields.length) return fallback;
	return (candidate) => candidateSearchValues(candidate, fields);
}

/**
 * Name the field a query matched, so the answer can say where it was found.
 *
 * Only reports a field other than the name: "AP-Lobby" found by its name
 * needs no explanation, while the same record found by a MAC address does.
 *
 * @param {object} candidate normalized candidate
 * @param {Array<object>} fields normalized field descriptors
 * @param {string} query user-entered query
 * @return {object} candidate, with the matched field named when there is one
 */
export function withMatchedField(candidate, fields, query) {
	const expected = String(query || '').toLocaleLowerCase().trim();
	if (!expected || !fields.length || !candidate?.field_values) return candidate;
	const name = String(candidate.name || '').toLocaleLowerCase();
	if (name === expected || name.includes(expected)) return candidate;
	for (const field of fields) {
		const stored = candidate.field_values[field.id];
		if (typeof stored !== 'string' || !stored) continue;
		const matched = namesForValue(field, stored).some((value) => {
			const folded = String(value).toLocaleLowerCase();
			return folded === expected || folded.includes(expected);
		});
		if (matched) {
			return {
				...candidate,
				matched_field_id: field.id,
				matched_field_label: field.label,
				matched_value: stored,
			};
		}
	}
	return candidate;
}

/**
 * Resolve candidates from Canvas rows using the catalogue the agent sent.
 *
 * The one place the catalogue is applied: every resource lookup reads its
 * rows, ranks them and reports what matched through this, so none of them
 * carries its own copy of the rules.
 *
 * @param {object} options resolution options
 * @param {Array<object>} options.rows Canvas rows
 * @param {Function} options.normalizeRow Canvas row normalizer
 * @param {string} options.query user-entered query
 * @param {number} options.limit maximum results
 * @param {*} options.searchFields field catalogue sent with the lookup
 * @param {Function} options.rank ranking function
 * @param {Function} [options.fallbackValues] resource value reader
 * @param {boolean} [options.fuzzy] allow near-miss matches
 * @return {Array<object>} ranked candidates
 */
export function resolveByFields({
	rows,
	normalizeRow,
	query,
	limit,
	searchFields,
	rank,
	fallbackValues,
	fuzzy = false,
}) {
	const fields = normalizeSearchFields(searchFields);
	const candidates = rows
		.map((row) => withFieldValues(normalizeRow(row), row, fields))
		.filter(Boolean);
	if (!query) return candidates.slice(0, limit);
	const ranked = rank(
		candidates,
		query,
		limit,
		searchValueReader(fields, fallbackValues),
		{fuzzy},
	);
	return ranked.map((candidate) => withMatchedField(candidate, fields, query));
}
